import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { TeamProvider } from './contexts/TeamContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { PrivateRoute } from './components/PrivateRoute';
import { LoadingSpinner } from './components/LoadingSpinner';
import './App.css';

// 遅延ロード（コード分割）
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Signup = lazy(() => import('./pages/Signup').then(m => ({ default: m.Signup })));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })));
const Terms = lazy(() => import('./pages/Terms').then(m => ({ default: m.Terms })));
const HowTo = lazy(() => import('./pages/HowTo').then(m => ({ default: m.HowTo })));
const Home = lazy(() => import('./pages/Home').then(m => ({ default: m.Home })));
const TeamSelection = lazy(() => import('./pages/TeamSelection').then(m => ({ default: m.TeamSelection })));
const TeamMembers = lazy(() => import('./pages/TeamMembers').then(m => ({ default: m.TeamMembers })));
const TeamPlayers = lazy(() => import('./pages/TeamPlayers').then(m => ({ default: m.TeamPlayers })));
const TeamSettings = lazy(() => import('./pages/TeamSettings').then(m => ({ default: m.TeamSettings })));
const Statistics = lazy(() => import('./pages/Statistics').then(m => ({ default: m.Statistics })));
const GameCreate = lazy(() => import('./pages/GameCreate').then(m => ({ default: m.GameCreate })));
const GameSetupNew = lazy(() => import('./pages/GameSetupNew').then(m => ({ default: m.GameSetupNew })));
const GameDetail = lazy(() => import('./pages/GameDetail').then(m => ({ default: m.GameDetail })));
const Record = lazy(() => import('./pages/Record').then(m => ({ default: m.Record })));
const SubstitutionScreen = lazy(() => import('./pages/SubstitutionScreen').then(m => ({ default: m.SubstitutionScreen })));
const SubstitutionDemo = lazy(() => import('./pages/SubstitutionDemo').then(m => ({ default: m.SubstitutionDemo })));
const PinchHitRunScreen = lazy(() => import('./pages/PinchHitRunScreen').then(m => ({ default: m.PinchHitRunScreen })));
const LineupSheet = lazy(() => import('./pages/LineupSheet').then(m => ({ default: m.LineupSheet })));
const JoinTeam = lazy(() => import('./pages/JoinTeam').then(m => ({ default: m.JoinTeam })));

// 古いコンポーネント（互換性のため）
const GameScreen = lazy(() => import('./components/GameScreen').then(m => ({ default: m.GameScreen })));
const GameSetup = lazy(() => import('./pages/GameSetup').then(m => ({ default: m.GameSetup })));
const BattingOrderScreen = lazy(() => import('./components/BattingOrderScreen').then(m => ({ default: m.BattingOrderScreen })));
const GameDetailScreen = lazy(() => import('./components/GameDetailScreen').then(m => ({ default: m.GameDetailScreen })));

// ローディングフォールバック
const PageLoader = () => (
  <div className="min-h-screen bg-gradient-to-br from-green-900 via-green-800 to-green-900 flex items-center justify-center">
    <LoadingSpinner size="lg" />
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
      <AuthProvider>
        <ToastProvider>
          <TeamProvider>
          <Suspense fallback={<PageLoader />}>
          <Routes>
          {/* ログイン関連のルートは保護しない */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/howto" element={<HowTo />} />

          {/* 招待URL（認証状態で分岐するため保護しない） */}
          <Route path="/join" element={<JoinTeam />} />

          {/* デモ用ルート（認証不要） */}
          <Route path="/demo/substitution" element={<SubstitutionDemo />} />

          {/* 保護が必要なルート */}
          <Route path="/" element={<PrivateRoute><Navigate to="/home" replace /></PrivateRoute>} />
          <Route path="/team-selection" element={<PrivateRoute><TeamSelection /></PrivateRoute>} />
          <Route path="/home" element={<PrivateRoute><Home /></PrivateRoute>} />
          <Route path="/statistics" element={<PrivateRoute><Statistics /></PrivateRoute>} />
          <Route path="/team/players" element={<PrivateRoute><TeamPlayers /></PrivateRoute>} />
          <Route path="/team/settings" element={<PrivateRoute><TeamSettings /></PrivateRoute>} />
          <Route path="/team-members" element={<PrivateRoute><TeamMembers /></PrivateRoute>} />
          <Route path="/batting-order" element={<PrivateRoute><BattingOrderScreen /></PrivateRoute>} />
          <Route path="/game/create" element={<PrivateRoute><GameCreate /></PrivateRoute>} />
          <Route path="/game/:gameId/setup" element={<PrivateRoute><GameSetupNew /></PrivateRoute>} />
          <Route path="/game/:gameId/substitution" element={<PrivateRoute><SubstitutionScreen /></PrivateRoute>} />
          <Route path="/game/:gameId/pinch-hit-run" element={<PrivateRoute><PinchHitRunScreen /></PrivateRoute>} />
          <Route path="/game/:gameId/lineup" element={<PrivateRoute><LineupSheet /></PrivateRoute>} />
          <Route path="/game/:gameId/detail" element={<PrivateRoute><GameDetail /></PrivateRoute>} />
          <Route path="/game/:gameId" element={<PrivateRoute><GameScreen /></PrivateRoute>} />
          <Route path="/game-setup" element={<PrivateRoute><GameSetup /></PrivateRoute>} />
          <Route path="/game/detail" element={<PrivateRoute><GameDetailScreen /></PrivateRoute>} />
          <Route path="/game" element={<PrivateRoute><Record /></PrivateRoute>} />
          </Routes>
          </Suspense>
          </TeamProvider>
        </ToastProvider>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
